export interface Kpis { revenue: number; orders: number; aov: number; returnsCount: number; returnsAmount: number }
export interface DailyPoint { date: string; revenue: number; orders: number }
export interface TopItem { name: string; qty: number; revenue: number }

export type TopAxis = 'item' | 'brand' | 'group' | 'category' | 'subgroup';

export interface Summary {
  from: string; to: string;
  scopedStores: string[];
  /** True when KPIs are summed across >1 store (i.e. "All stores" mode). */
  isAggregate?: boolean;
  kpis: Kpis;
  daily: DailyPoint[];
  /** @deprecated use topBy.item — retained for old clients. */
  topItems: TopItem[];
  /** Top-10-by-revenue across each axis. Server returns all four in one round-trip. */
  topBy: Record<TopAxis, TopItem[]>;
}

export interface BillRow {
  billNo: string; billDate: string; storeCode: string;
  netAmt: number; billAmt: number; tender: string; custCode: number | null;
}
export interface BillsPage { page: number; limit: number; total: number; bills: BillRow[] }

export interface SalesmanReportRow {
  kind: 'sale' | 'return';
  salesmanId: string;
  salesmanName: string;
  user: string;
  date: string;
  time: string;
  barcode: string;
  itemName: string;
  brand: string;
  artNo: string;
  color: string;
  size: string;
  price: number;
  commission: number;
  subgroup: string;
  storeCode: string;
  billNo: string;
  /** True if a corresponding INV_SALESUB / INV_SALESRETURNSUB row exists in the same period — i.e. the scan made it onto a committed bill. */
  billed: boolean;
  /**
   * Salesman's commission rate (`%`) from `INV_EMPMASTER.COMMPERCENT`, applied
   * to the period net (sales − returns). 0 if the salesman has no master row.
   */
  commPercent: number;
  /**
   * Sole-piece incentive line — the flat bonus for shifting dead stock
   * (POS commission tier 1). Always counted in `commission`, so totals were
   * already right; this just lets the breakdown show it separately.
   */
  solePiece: boolean;
}
export interface SalesmanAgg {
  salesmanId: string; name: string;
  qtySold: number; amtSold: number;
  qtyReturn: number; amtReturn: number;
  netQty: number; netAmt: number;
  distinctBrands: number; distinctArticles: number;
  /** Per-line brand commission summed from `INV_SALESMANBARCODE.COMMISSION`. */
  brandCommission: number;
  /**
   * The sole-piece slice of `brandCommission` — the flat dead-stock incentive.
   * Already inside brandCommission (and so inside `commission`); broken out so
   * the breakdown can show what was earned on dead stock.
   */
  solePieceCommission: number;
  /** Scans that earned the sole-piece incentive. */
  solePieceQty: number;
  /** `commPercent × netAmt / 100`, integer-truncated (matches legacy). */
  pctCommission: number;
  /** Salesman's COMMPERCENT (single rate per active master row). */
  commPercent: number;
  /** brand + pct. */
  commission: number;
  /** Sale scans that never made it onto a committed bill — counted separately so KPIs match legacy. */
  qtyUnbilled: number; amtUnbilled: number;
}
export interface SalesmanGrand {
  qtySold: number; amtSold: number;
  qtyReturn: number; amtReturn: number;
  netQty: number; netAmt: number;
  brandCommission: number;
  /** Sole-piece slice of brandCommission, summed across salesmen. */
  solePieceCommission: number;
  solePieceQty: number;
  pctCommission: number;
  /** Sum across salesmen (no single rate at the grand level). */
  commission: number;
  qtyUnbilled: number; amtUnbilled: number;
}
export interface SalesmanReport {
  from: string; to: string;
  totalRows: number;
  truncated: boolean;
  rows: SalesmanReportRow[];
}

function qs(params: Record<string, string | number | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') u.set(k, String(v));
  return u.toString();
}

// `store` query param selects the source DB (= store identity).
// Per-DB internal STORE_CODE column is no longer a selector; it survives only
// as a returned data field on bill rows.

export interface SalesMasters {
  item: string[];
  brand: string[];
  /** Article / style codes. */
  style: string[];
  category: string[];
}

/** Match mode for the dashboard's text filters — mirrors the stock ledger. */
export type MatchMode = 'contains' | 'startswith' | 'similar';

/**
 * Filters that narrow the "Top by revenue" breakdown. They deliberately do NOT
 * affect the KPI row: revenue/orders/AOV/returns are per-BILL figures, and a
 * bill filtered down to a single article is no longer an order.
 */
export interface TopFilters {
  item?: string | null;
  brand?: string | null;
  category?: string | null;
  style?: string | null;
  q?: string | null;
  match?: MatchMode;
}

export async function getSalesMasters(): Promise<SalesMasters> {
  const r = await fetch('/api/sales/masters', { credentials: 'include' });
  if (!r.ok) throw new Error(`sales masters failed (${r.status})`);
  return r.json() as Promise<SalesMasters>;
}

export async function getSummary(
  p: { from: string; to: string; store?: string | null } & TopFilters,
): Promise<Summary> {
  const r = await fetch(`/api/sales/summary?${qs({
    from: p.from, to: p.to, store: p.store ?? undefined,
    item: p.item ?? undefined,
    brand: p.brand ?? undefined,
    category: p.category ?? undefined,
    style: p.style ?? undefined,
    q: p.q ?? undefined,
    match: p.match && p.match !== 'contains' ? p.match : undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`summary failed (${r.status})`);
  return r.json() as Promise<Summary>;
}

export async function getBills(p: { from: string; to: string; store?: string | null; page: number; limit: number }): Promise<BillsPage> {
  const r = await fetch(`/api/sales/bills?${qs({ from: p.from, to: p.to, store: p.store ?? undefined, page: p.page, limit: p.limit })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`bills failed (${r.status})`);
  return r.json() as Promise<BillsPage>;
}

// Returns list mirrors bills (against INV_SALESRETURN) — same row shape.
export interface ReturnsPage { page: number; limit: number; total: number; returns: BillRow[] }

export async function getReturns(p: { from: string; to: string; store?: string | null; page: number; limit: number }): Promise<ReturnsPage> {
  const r = await fetch(`/api/sales/returns?${qs({ from: p.from, to: p.to, store: p.store ?? undefined, page: p.page, limit: p.limit })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`returns failed (${r.status})`);
  return r.json() as Promise<ReturnsPage>;
}

// Barcode Scan Summary. Source: INV_REMOVEBARCODE.
// kind: 'sale' = sale-side scan flow (TRANTYPE='S'); 'purchase' = purchase-side
// scan flow (TRANTYPE='P'). Mirrors the legacy desktop report's SALE/PURCHASE
// radio.
export interface ScanSummaryRow {
  kind: 'sale' | 'purchase';
  trantype: string; // 'S' | 'P'
  date: string;
  time: string;
  barcode: string;
  itemName: string;
  brand: string;
  artNo: string;
  color: string;
  size: string;
  sp: number;
  mrp: number;
  posUser: string;
  salesmanId: string;
  salesmanName: string;
  scanCount: number;
}
export interface ScanSummaryTotals {
  saleCount: number;     saleAmount: number;
  purchaseCount: number; purchaseAmount: number;
}
export interface ScanSummary {
  from: string; to: string;
  type: 'ALL' | 'S' | 'P';
  /** Selected POS user, or null for all. */
  posUser: string | null;
  /** Every POS user in the range, before filtering — for the dropdown. */
  posUsers: string[];
  totalRows: number;
  rows: ScanSummaryRow[];
  totals: ScanSummaryTotals;
}

export async function getScanSummary(p: {
  from: string; to: string;
  store?: string | null;
  type?: 'ALL' | 'S' | 'P';
  posUser?: string | null;
}): Promise<ScanSummary> {
  const r = await fetch(`/api/sales/scan-summary?${qs({
    from: p.from, to: p.to,
    store: p.store ?? undefined,
    type: p.type ?? 'ALL',
    posUser: p.posUser ?? undefined,
  })}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`scan summary failed (${r.status})`);
  return r.json() as Promise<ScanSummary>;
}

export async function getSalesmanReport(p: {
  from: string; to: string;
  store?: string | null;
}): Promise<SalesmanReport> {
  const r = await fetch(
    `/api/sales/salesman-report?${qs({ from: p.from, to: p.to, store: p.store ?? undefined })}`,
    { credentials: 'include' }
  );
  if (!r.ok) throw new Error(`salesman report failed (${r.status})`);
  return r.json() as Promise<SalesmanReport>;
}

export interface RefreshCacheResult {
  store: string;
  from: string; to: string;
  which: 'salesman' | 'estimate' | 'all';
  daysRefreshed: number;
  totalDurationMs: number;
  results: Array<{ date: string; kind: 'salesman' | 'estimate'; rowsWritten: number; durationMs: number }>;
}

export async function refreshCache(p: {
  from: string; to: string;
  store?: string | null;
  which?: 'salesman' | 'estimate' | 'all';
}): Promise<RefreshCacheResult> {
  const r = await fetch(`/api/sales/refresh-cache?${qs({
    from: p.from, to: p.to,
    store: p.store ?? undefined,
    which: p.which ?? 'all',
  })}`, { method: 'POST', credentials: 'include' });
  if (!r.ok) throw new Error(`refresh failed (${r.status}): ${await r.text()}`);
  return r.json() as Promise<RefreshCacheResult>;
}

export function exportUrl(p: { from: string; to: string; store?: string | null }): string {
  return `/api/sales/export.csv?${qs({ from: p.from, to: p.to, store: p.store ?? undefined })}`;
}

export interface BillHeader {
  billNo: string; billDate: string; storeCode: string; storeName: string | null;
  custCode: number | null; custName: string | null;
  salesman: string | null; tender: string | null;
}
export interface BillTotals {
  netAmt: number; fDscntAmt: number; mdAmt: number; dscntAmt: number;
  vat1: number; vat2: number; vat3: number;
  billAmt: number; refundAmt: number; alterAmt: number;
}
export interface BillPayment {
  cashAmt: number; cardAmt: number; cardLast4: string | null; cashCredit: string | null;
}
export interface BillLine {
  barcode: string; itemCode: string; itemName: string;
  color: string | null; size: string | null;
  qty: number; mrp: number; sp: number;
  fDscntAmt: number; mdAmt: number; dscntAmt: number; netAmt: number;
}
export interface BillDetail {
  /** 'sale' = INV_SALE-backed; 'return' = INV_SALESRETURN-backed. */
  kind: 'sale' | 'return';
  header: BillHeader;
  totals: BillTotals;
  payment: BillPayment;
  lines: BillLine[];
}

export async function getBillDetail(p: {
  storeCode: string; billDate: string; billNo: string;
  store?: string | null;
}): Promise<BillDetail> {
  const url = `/api/sales/bills/${encodeURIComponent(p.storeCode)}/${encodeURIComponent(p.billDate)}/${encodeURIComponent(p.billNo)}?${qs({ store: p.store ?? undefined })}`;
  const r = await fetch(url, { credentials: 'include' });
  if (r.status === 404) throw new Error('NOT_FOUND');
  if (r.status === 403) throw new Error('FORBIDDEN');
  if (!r.ok) throw new Error(`bill detail failed (${r.status})`);
  return r.json() as Promise<BillDetail>;
}
